// Shapes from openapi.yaml — the contract is the source, this mirrors it.

export interface Money {
  minor: number;
  currency: string;
}

// A supplier's review aggregate (DECISION-056): the average (one decimal) and
// the count. { average: 0, count: 0 } means no reviews yet — never a fabricated
// score.
export interface ReviewAggregate {
  average: number;
  count: number;
}

// The hotel's review of a completed booking (DECISION-056): 1-5 + an optional
// note. Carried on the Booking once rated; both sides read it.
export interface Review {
  rating: number;
  note?: string;
  at: string;
}

// GET /v1/account — the caller's own record. The timezone is an IANA zone;
// offsets are derived from it at the date of use (ui.zoneOffset), never stored.
export interface Account {
  id: string;
  name: string;
  type: 'hotel' | 'supplier';
  timezone: string;
  currency?: string;
  // The canon-11 step-3 business basics (S11), absent until the supplier sets them.
  categories?: string[];
  location?: { place: string; lat?: number; lng?: number };
  languages?: string[];
  // The supplier's review aggregate (DECISION-056) — present for a supplier
  // (0/0 before the first review), absent for a hotel.
  rating?: ReviewAggregate;
}

// GET /v1/onboarding — organic onboarding's "what's left" state (canon 11 E2).
export interface Onboarding {
  status: string;
  steps: { basics: boolean; product: boolean; availability: boolean };
  missing: ('basics' | 'product' | 'availability')[];
  complete: boolean;
}

// The supplier's recurring working pattern (canon 11 step 5).
export interface AvailabilityPattern {
  days: number[]; // ISO weekdays 1=Mon … 7=Sun
  startTime?: string;
  endTime?: string;
  seasonStartMonth?: number;
  seasonEndMonth?: number;
}

export type BookingStatus =
  | 'requested'
  | 'pending'
  | 'confirmed'
  | 'alternative'
  | 'declined'
  | 'cancelled'
  | 'completed';

export interface TimelineEntry {
  at: string;
  actor: string;
  from?: BookingStatus;
  to: BookingStatus;
  reason?: string;
  note?: string;
}

export interface Booking {
  id: string;
  hotelAccountId: string;
  supplierAccountId: string;
  // The urgency ladder's visible state, stamped by the sweeper and shown to
  // BOTH sides. Absent means quiet or answered.
  chase?: { state: 'surfaced' | 'escalated' | 'expired'; at: string };
  cancelReason?: string;
  hotelName?: string;
  supplierName?: string;
  productId: string;
  productName?: string;
  // The product kind (category), stamped at create (P10 / DECISION-058).
  kind?: 'experience' | 'transfer';
  status: BookingStatus;
  start: string;
  end: string;
  guests: { adults: number; children: number };
  guestName: string;
  guestRoom?: string;
  notes?: string;
  supplierAmount: Money;
  guestAmount?: Money;
  margin?: Money;
  commission?: Money; // hotel view only — the ISO fee accrued at completion (DECISION-052)
  // The cancellation fee (P10 / DECISION-058), present once the hotel cancelled a
  // confirmed booking inside the window — shown to both sides, its own line.
  cancelFee?: Money;
  // What the hotel would owe if it cancelled this confirmed booking now — present
  // only on the hotel's GET /bookings/{id} while confirmed and inside the window.
  cancelFeePreview?: Money;
  answerDeadline?: string;
  proposedStart?: string;
  proposedEnd?: string;
  holdExpiresAt?: string;
  declineReason?: string;
  // The hotel's review of a completed job (DECISION-056), present once rated.
  // Shown to both sides — the supplier reads the rating, the hotel sees it has
  // already rated (the CTA clears).
  review?: Review;
  undo?: { until: string };
  createdAt: string;
  updatedAt: string;
  timeline?: TimelineEntry[];
}

export interface Product {
  id: string;
  supplierId: string;
  supplierName?: string;
  name: string;
  kind: 'experience' | 'transfer';
  durationHours?: number;
  maxGuests?: number;
  supplierPrice: Money;
  per: 'guest' | 'job';
  margin?: Money; // hotel view only
  guestPrice?: Money; // hotel view only
  exposed?: boolean; // hotel view only, scope=all — whether it's in the booked catalogue
}

export interface AvailabilityDay {
  date: string;
  available: boolean;
  reason?: string;
}

// Eva (canon 18). A grant is a named capability, on or off; the accept grant
// is scoped by rules (the predicates). The audit derives from the eva: stamps
// on the supplier's own bookings — the timeline is the record.
export type GrantKey = 'tell' | 'draft' | 'money' | 'accept' | 'propose' | 'block';

export interface EvaGrant {
  key: GrantKey;
  on: boolean;
  suspended?: boolean;
  suspendedReason?: string;
  updatedAt?: string;
}

export interface EvaRule {
  id: string;
  label?: string;
  hotels?: string[];
  products?: string[];
  maxGuests?: number;
  maxValueMinor?: number;
  dateFrom?: string;
  dateTo?: string;
  createdAt: string;
}

export interface EvaAction {
  bookingId: string;
  capability: string;
  actor: string;
  via?: string; // the grant/rule that authorised it — the "why"
  at: string;
  to: BookingStatus;
  hotelName?: string;
  productName?: string;
  start: string;
  status: BookingStatus;
  canUndo: boolean;
}

export interface Connection {
  accountId?: string;
  hotelAccountId?: string;
  supplierAccountId?: string;
  hotelName?: string;
  supplierName?: string;
  name?: string;
  status: string;
}

export const DECLINE_REASONS: { code: string; label: string }[] = [
  { code: 'already_booked', label: 'Already booked' },
  { code: 'weather', label: 'Weather' },
  { code: 'maintenance', label: 'Boat maintenance' },
  { code: 'too_many_guests', label: 'Too many guests' },
  { code: 'short_notice', label: 'Not enough notice' },
  { code: 'other', label: 'Something else…' },
];
